import { useState } from 'react';
import { toast } from 'react-toastify';
import type React from 'react';

import type { RelatorioCivelFormData, SustentacaoOralItem } from '../relatorio-civel-page.models';

type UseSustentacaoOralParams = {
  formData: RelatorioCivelFormData;
  setFormData: React.Dispatch<React.SetStateAction<RelatorioCivelFormData>>;
  isViewMode: boolean;
};

export function useSustentacaoOral({ formData, setFormData, isViewMode }: UseSustentacaoOralParams) {
  const [novoNumeroProcessoSustentacao, setNovoNumeroProcessoSustentacao] = useState('');
  const [novaParteSustentacao, setNovaParteSustentacao] = useState('');

  const [editandoSustentacaoOral, setEditandoSustentacaoOral] = useState<string | null>(null);
  const [valorEdicaoNumeroProcessoSustentacao, setValorEdicaoNumeroProcessoSustentacao] = useState('');
  const [valorEdicaoParteSustentacao, setValorEdicaoParteSustentacao] = useState('');

  const adicionarSustentacaoOral = () => {
    if (isViewMode) return;

    if (!novoNumeroProcessoSustentacao.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!novaParteSustentacao.trim()) {
      toast.warning('Informe a parte.');
      return;
    }

    const novoItem: SustentacaoOralItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      numeroProcesso: novoNumeroProcessoSustentacao.trim(),
      parte: novaParteSustentacao.trim(),
      dataAdicao: new Date(),
    };

    setFormData((prev) => ({
      ...prev,
      sustentacaoOral: {
        itens: [...prev.sustentacaoOral.itens, novoItem],
      },
    }));

    setNovoNumeroProcessoSustentacao('');
    setNovaParteSustentacao('');
    toast.success('Sustentação oral adicionada com sucesso!');
  };

  const removerSustentacaoOral = (id: string) => {
    if (isViewMode) return;

    setFormData((prev) => ({
      ...prev,
      sustentacaoOral: {
        itens: prev.sustentacaoOral.itens.filter((item) => item.id !== id),
      },
    }));
    toast.success('Sustentação oral removida com sucesso!');
  };

  const iniciarEdicaoSustentacaoOral = (item: SustentacaoOralItem) => {
    if (isViewMode) return;
    setEditandoSustentacaoOral(item.id);
    setValorEdicaoNumeroProcessoSustentacao(item.numeroProcesso);
    setValorEdicaoParteSustentacao(item.parte);
  };

  const cancelarEdicaoSustentacaoOral = () => {
    setEditandoSustentacaoOral(null);
    setValorEdicaoNumeroProcessoSustentacao('');
    setValorEdicaoParteSustentacao('');
  };

  const salvarEdicaoSustentacaoOral = (id: string) => {
    if (isViewMode) return;

    if (!valorEdicaoNumeroProcessoSustentacao.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!valorEdicaoParteSustentacao.trim()) {
      toast.warning('Informe a parte.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      sustentacaoOral: {
        itens: prev.sustentacaoOral.itens.map((item) =>
          item.id === id
            ? {
                ...item,
                numeroProcesso: valorEdicaoNumeroProcessoSustentacao.trim(),
                parte: valorEdicaoParteSustentacao.trim(),
              }
            : item,
        ),
      },
    }));

    cancelarEdicaoSustentacaoOral();
    toast.success('Sustentação oral atualizada com sucesso!');
  };

  const adicionarMultiplasLinhasSustentacaoOral = (quantidade: number) => {
    if (isViewMode) return;

    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: SustentacaoOralItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        numeroProcesso: '',
        parte: '',
        dataAdicao: new Date(),
      });
    }

    setFormData((prev) => ({
      ...prev,
      sustentacaoOral: {
        itens: [...prev.sustentacaoOral.itens, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  return {
    novoNumeroProcessoSustentacao,
    setNovoNumeroProcessoSustentacao,
    novaParteSustentacao,
    setNovaParteSustentacao,
    editandoSustentacaoOral,
    valorEdicaoNumeroProcessoSustentacao,
    setValorEdicaoNumeroProcessoSustentacao,
    valorEdicaoParteSustentacao,
    setValorEdicaoParteSustentacao,
    adicionarSustentacaoOral,
    removerSustentacaoOral,
    iniciarEdicaoSustentacaoOral,
    salvarEdicaoSustentacaoOral,
    cancelarEdicaoSustentacaoOral,
    adicionarMultiplasLinhasSustentacaoOral,
    totalSustentacoesOral: formData.sustentacaoOral.itens.length,
  };
}
